import Button from "@/comps/button"
import Crisp from "@/comps/crisp"
import Footer from "@/comps/footer"
import Header from "@/comps/header"
import Input, {
  Textarea,
  useValidator,
} from "@/comps/input"
import {
  ErrorMessage,
  SuccessMessage,
  WarningMessage,
} from "@/comps/message"
import Meta from "@/comps/meta"
import Radios from "@/comps/radio"
import Tooltip from "@/comps/tooltip"

import { generateServerSideProps } from "@/ssr/common"

import { devices } from "@/utils/breakpoints"
import { NotificationContext } from "@/utils/notification"
import { isEmail, notEmpty } from "@/utils/validators"

import axios, { AxiosError } from "axios"
import { useTranslation } from "next-i18next"
import {
  FormEvent,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { Box } from "react-styled-flex"
import styled from "styled-components"

import { Request } from "types/endpoints/contact"

const Wrapper = styled.div`
  margin: 0 auto;

  width: calc(var(--w-screen) - 32px);
  min-height: calc(100vh - 540px);

  @media ${devices.tablet} {
    width: 605px;
  }

  @media ${devices.desktop} {
    width: 1175px;
  }
`

const Container = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column-reverse;

  align-items: flex-start;

  gap: 16px;

  @media ${devices.desktop} {
    flex-direction: row;
    gap: 25px;
  }
`

const Title = styled.h1`
  text-transform: capitalize;
  text-align: center;

  font-weight: 700;
  font-size: 18px;
  line-height: 21px;

  margin: 16px 0;

  @media ${devices.tablet} {
    font-size: 24px;
    line-height: 29px;

    margin: 12px 0 21px 0;
  }

  @media ${devices.desktop} {
    font-weight: 800;
    font-size: 36px;
    line-height: 52px;

    margin-bottom: 48px;
  }
`

const Block = styled.div`
  display: flex;
  flex-direction: column;

  gap: 16px;

  padding: 24px;
  border-radius: 12px;

  background-color: var(--color-background-cell);
`

const Label = styled.h2`
  font-weight: 600;
  font-size: 18px;
  line-height: 21px;

  @media ${devices.tablet} {
    font-size: 22px;
    line-height: 26px;
  }
`

const TextContent = styled.p`
  font-weight: 400;
  font-size: 16px;
  line-height: 25px;

  @media ${devices.tablet} {
    font-weight: 500;
  }
`

const ContactTextContent = styled(TextContent)`
  max-width: 300px;
`

const ContactForm = styled(Block)`
  width: 100%;

  @media ${devices.tablet} {
    width: 605px;
  }

  @media ${devices.desktop} {
    width: 375px;
  }
`

const RadioContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 22px;

  align-self: center;

  @media ${devices.desktop} {
    align-self: flex-start;
  }
`

const Help = styled(Block)`
  width: 100%;

  @media ${devices.tablet} {
    width: 605px;
  }

  @media ${devices.desktop} {
    width: 775px;
  }
`

const Line = styled.div`
  display: none;

  @media ${devices.tablet} {
    display: block;

    width: calc(100% + 48px);
    height: 1px;

    background-color: var(--color-separator);
    margin: 0 -24px;
  }
`

const EmailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;

  @media ${devices.tablet} {
    height: 170px;

    column-gap: 80px;
    row-gap: 24px;
  }
`

const SingleEmail = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 2px;

  list-style: disc inside;

  padding: 12px 0;
  border-top: 1px solid var(--color-separator);

  @media ${devices.tablet} {
    padding: 0;
    border-top: unset;
  }
`

const EmailLabel = styled.li`
  font-weight: 600;
  font-size: 15px;
  line-height: 19px;

  color: var(--color-blue-600);

  text-transform: uppercase;

  span {
    margin-left: -8px;
  }

  @media ${devices.tablet} {
    span {
      margin-left: unset;
    }
  }
`

const EmailAddress = styled.a`
  align-self: flex-start;
  letter-spacing: 0.04em;

  font-weight: 500;
  font-size: 16px;
  line-height: 19px;

  color: var(--color-blue-900);
`

const InputContainer = styled.form`
  display: flex;
  flex-direction: column;
`

const TabletRow = styled.div`
  display: flex;
  flex-direction: column;

  @media ${devices.tablet} {
    flex-direction: row;
    gap: 24px;
  }

  @media ${devices.desktop} {
    flex-direction: column;
    gap: 0;
  }
`

const NameInput = styled(Input)`
  input {
    text-transform: capitalize;
  }

  flex: 1;
`

const PhoneInput = styled(Input)`
  flex: 1;
`

const Contact = () => {
  const { t } = useTranslation("page-contact")

  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")
  const [email, setEmail] = useState("")
  const [message, setMessage] = useState("")
  const [intention, setIntention] = useState(1)
  const [loading, setLoading] = useState(false)

  const { validate: nameValidate, errors: nameErrors } =
    useValidator(name, [notEmpty])

  const { validate: emailValidate, errors: emailErrors } =
    useValidator(email, [notEmpty, isEmail])
  const {
    validate: messageValidate,
    errors: messageErrors,
  } = useValidator(message, [notEmpty])

  const formRef = useRef<HTMLFormElement>(null)

  const { notify } = useContext(NotificationContext)

  const anyError = !(
    name &&
    email &&
    message &&
    !nameErrors &&
    !emailErrors &&
    !messageErrors
  )

  const { executeRecaptcha } = useGoogleReCaptcha()

  const submitHandler = useCallback(
    async (e: FormEvent) => {
      e.preventDefault()

      if (loading)
        return notify(
          <WarningMessage>
            {t("common:txt_loading_wait")}
          </WarningMessage>,
        )

      if (anyError)
        return notify(
          <WarningMessage>
            {t("common:txt_please_fill_input")}
          </WarningMessage>,
        )

      if (executeRecaptcha === undefined)
        return notify(
          <WarningMessage>
            {t("common:txt_recaptcha_not_loaded")}
          </WarningMessage>,
        )

      setLoading(true)

      try {
        const captcha = await executeRecaptcha()

        const data: Request = {
          name,
          phone: phone || null,
          email,
          message,
          captcha,
          intention: intention ? "individual" : "business",
        }

        await axios.post("/api/contact", data)

        notify(
          <SuccessMessage>
            {t("common:txt_contact_success")}
          </SuccessMessage>,
        )
      } catch (e) {
        if (
          e instanceof AxiosError &&
          e.response?.data.code === "logic"
        ) {
          notify(
            <ErrorMessage>
              {t("common:txt_captcha_failed")}
            </ErrorMessage>,
          )
        } else {
          notify(
            <ErrorMessage>
              {t("common:txt_contact_error")}
            </ErrorMessage>,
          )
        }
      }

      setLoading(false)
      setName("")
      setPhone("")
      setEmail("")
      setMessage("")
    },
    [
      anyError,
      email,
      executeRecaptcha,
      intention,
      loading,
      message,
      name,
      notify,
      phone,
      t,
    ],
  )

  return (
    <>
      <Meta
        title={t("page-contact:txt_page_title")}
        description={t("page-contact:txt_page_description")}
      />
      <Header />
      <Wrapper>
        <Title>
          {t("lbl_contact_us", { ns: "common" })}
        </Title>
        <Container>
          <ContactForm>
            <Label>{t("common:txt_get_in_touch")}</Label>
            <ContactTextContent>
              {t("txt_have_an_inquiry")}
            </ContactTextContent>
            <InputContainer
              onSubmit={submitHandler}
              ref={formRef}>
              <TabletRow>
                <NameInput
                  validate={nameValidate}
                  errors={nameErrors}
                  value={name}
                  setValue={setName}
                  label={t("common:txt_full_name")}
                  type="text"
                  name="name"
                />
                <PhoneInput
                  value={phone}
                  setValue={setPhone}
                  label={t(
                    "common:txt_phone_number_optional",
                  )}
                  type="tel"
                  name="phone"
                />
              </TabletRow>
              <Input
                validate={emailValidate}
                errors={emailErrors}
                value={email}
                setValue={setEmail}
                label={t("common:txt_email_addr")}
                type="email"
                name="email"
              />
              <Textarea
                validate={messageValidate}
                errors={messageErrors}
                label={t("txt_your_message")}
                value={message}
                setValue={setMessage}
                rows={3}
              />
              <Box height={12} />
              <Tooltip
                title={t("common:txt_please_fill")}
                disabled={Boolean(anyError)}>
                <Button
                  loading={loading}
                  disabled={Boolean(anyError) || loading}
                  primary
                  text="page-contact:lbl_submit"
                />
              </Tooltip>
            </InputContainer>
            <RadioContainer>
              <Radios
                labels={[
                  t("common:lbl_business"),
                  t("common:lbl_individual"),
                ]}
                name="type"
                checked={intention}
                onChange={setIntention}
              />
            </RadioContainer>
          </ContactForm>
          <Help>
            <Label>{t("txt_we_are_here")}</Label>
            <TextContent>
              {t("txt_any_questions")}
            </TextContent>
            <Line />
            <EmailsContainer>
              {[
                {
                  label: "for_joining",
                  address: "hr@zoomerang.app",
                },
                {
                  label: "for_technical_issues",
                  address: "support@zoomerang.app",
                },
                {
                  label: "for_feedback",
                  address: "feedback@zoomerang.app",
                },
                {
                  label: "for_marketing",
                  address: "promotion@zoomerang.app",
                },
                {
                  label: "for_general_questions",
                  address: "info@zoomerang.app",
                },
              ].map(({ label, address }, index) => (
                <SingleEmail key={index}>
                  <EmailLabel>
                    <span>{t("txt_" + label)}</span>
                  </EmailLabel>
                  <EmailAddress href={"mailto:" + address}>
                    {address}
                  </EmailAddress>
                </SingleEmail>
              ))}
            </EmailsContainer>
          </Help>
        </Container>
      </Wrapper>
      <Crisp page="contact" />
      <Footer />
    </>
  )
}

export const getServerSideProps = generateServerSideProps([
  "page-contact",
])

export default Contact
